import { Link } from 'react-router-dom';
import { WeekContent } from '../../hooks/useAstroCoach';

interface Props {
  content: WeekContent;
  onClose: () => void;
}

const WEEK_COLORS: Record<number, string> = {
  1: '#f59e0b', 2: '#c084fc', 3: '#60a5fa', 4: '#f472b6',
  5: '#f97316', 6: '#fbbf24', 7: '#94a3b8', 8: '#a78bfa',
  9: '#38bdf8', 10: '#c084fc', 11: '#34d399', 12: '#fde68a',
};

function renderBold(line: string): React.ReactNode {
  const parts = line.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part);
}

function renderMarkdown(text: string): React.ReactNode {
  return text.split('\n\n').map((para, i) => (
    <p key={i} style={{ marginBottom: '0.85rem', lineHeight: 1.75 }}>
      {renderBold(para)}
    </p>
  ));
}

export function WeekContentView({ content, onClose }: Props) {
  const color = WEEK_COLORS[content.week_number] ?? '#fbbf24';
  const generatedDate = content.generated_at_transit
    ? new Date().toLocaleDateString('pt-BR')
    : null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        backgroundColor: 'rgba(0,0,0,0.75)',
        overflowY: 'auto', padding: '1rem',
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 'var(--radius-card)',
          border: `1px solid ${color}44`,
          maxWidth: '760px', width: '100%',
          marginTop: '2rem', marginBottom: '4rem',
          padding: '2rem',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <p style={{ fontSize: 'var(--text-caption)', color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
              Semana {content.week_number} · {content.planet}
            </p>
            <h2 style={{ fontSize: 'var(--text-title)', color: 'var(--text-primary)', margin: 0 }}>{content.title}</h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.25rem', padding: '0.25rem' }}
            aria-label="Fechar"
          >✕</button>
        </div>

        {/* Sections */}
        {content.sections?.map(section => (
          <div key={section.id} style={{ marginBottom: '2rem' }}>
            <h3 style={{ color, fontSize: 'var(--text-body)', fontWeight: 700, marginBottom: '0.75rem', borderBottom: `1px solid ${color}33`, paddingBottom: '0.4rem' }}>
              {section.title}
            </h3>
            <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body)' }}>
              {renderMarkdown(section.content)}
            </div>
          </div>
        ))}

        {/* Mantra */}
        {content.mantra && (
          <div style={{ background: `${color}11`, border: `1px solid ${color}44`, borderRadius: 'var(--radius-sm)', padding: '1.25rem', marginBottom: '2rem', textAlign: 'center' }}>
            <p style={{ fontSize: 'var(--text-caption)', color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Mantra da Semana</p>
            <p style={{ fontStyle: 'italic', color: 'var(--text-primary)', fontSize: '1.05rem', lineHeight: 1.6 }}>"{content.mantra}"</p>
          </div>
        )}

        {/* Reflections */}
        {content.reflections?.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color, fontSize: 'var(--text-body)', fontWeight: 700, marginBottom: '0.75rem' }}>Reflexões da Semana</h3>
            <ol style={{ paddingLeft: '1.25rem', margin: 0 }}>
              {content.reflections.map((q, i) => (
                <li key={i} style={{ color: 'var(--text-secondary)', marginBottom: '0.6rem', lineHeight: 1.65 }}>{q}</li>
              ))}
            </ol>
          </div>
        )}

        {/* Practices */}
        {content.weekly_practice && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
            {[
              { label: 'Prática Diária', text: content.weekly_practice.daily },
              { label: 'Prática Semanal', text: content.weekly_practice.weekly },
            ].map(({ label, text }) => (
              <div key={label} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '1rem', border: '1px solid var(--border-subtle)' }}>
                <p style={{ fontSize: 'var(--text-caption)', color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>{label}</p>
                <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-small)', lineHeight: 1.65 }}>{text}</p>
              </div>
            ))}
          </div>
        )}

        {/* Cross-sell: Perguntar aos Astros */}
        <div style={{ border: '1px solid rgba(192,132,252,0.3)', background: 'rgba(192,132,252,0.06)', borderRadius: 'var(--radius-sm)', padding: '1.25rem', marginBottom: '1.5rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-small)', marginBottom: '0.5rem' }}>
            A análise da semana gerou uma dúvida específica? Consulte os astros diretamente.
          </p>
          <Link
            to="/questions"
            onClick={onClose}
            style={{ color: '#c084fc', fontWeight: 600, fontSize: 'var(--text-small)', textDecoration: 'none' }}
          >
            Perguntar aos Astros →
          </Link>
        </div>

        {/* Footer */}
        {generatedDate && (
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', textAlign: 'center', marginTop: '1rem' }}>
            Análise gerada em {generatedDate}
          </p>
        )}
      </div>
    </div>
  );
}
